import { Location } from '@angular/common';
import { Injectable, Type } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, EMPTY, Observable } from 'rxjs';
import {
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { SubSink } from 'subsink';
import { BreadcrumbService } from 'xng-breadcrumb';
import { AutoUnsubscribe } from '../../core/auto-unsubscribe';
import { UserInfoService } from '../../core/user-info.service';
import {
  DemandRequestRouteNames,
  InvitationRouteNames,
} from '../../shared/routing.constants';
import { byTextAscending } from '../../shared/sort';
import { RequestTypeEnum } from '../../shared/xatena-api/request-type';
import { TaskFormKeyEnum } from '../../shared/xatena-api/task-form-key';
import {
  AccountingXatSvc,
  ApproveTaskParamDto,
  CompaniesXatSvc,
  ConversationDto,
  ConversationReducedDto,
  DelegateSingleTaskParamDto,
  DelegateTaskParamDto,
  RekoleCategoryDto,
  RequestComplianceDTODto,
  RequestDto,
  RequestLogDto,
  RequestProductDto,
  RequestsXatSvc,
  StartConversationParamDto,
  TaskReducedDto,
} from '../../xatena-api';
import { UserRolesEnum } from './../../core/user-info.service';
import { RequestTypeAdvancedTrainingComponent } from './request-type-advanced-training/request-type-advanced-training.component';
import { RequestTypeApplianceLoanerComponent } from './request-type-appliance-loaner/request-type-appliance-loaner.component';
import { RequestTypeBaseComponent } from './request-type-base/request-type-base.component';
import { RequestTypeCatalogExtensionComponent } from './request-type-catalog-extension/request-type-catalog-extension.component';
import { RequestTypeCatalogProductLuksComponent } from './request-type-catalog-product/request-type-catalog-product-luks.component';
import { RequestTypeCatalogProductUszComponent } from './request-type-catalog-product/request-type-catalog-product-usz.component';
import { RequestTypeCatalogProductComponent } from './request-type-catalog-product/request-type-catalog-product.component';
import { RequestTypeHumanResourceRequirementComponent } from './request-type-human-resource-requirement/request-type-human-resource-requirement.component';
import { RequestTypeInstrumentsLoanerComponent } from './request-type-instruments-loaner/request-type-instruments-loaner.component';
import { RequestTypeInvestmentExceptionalSpzComponent } from './request-type-investment-exceptional/request-type-investment-exceptional-spz.component';
import { RequestTypeInvestmentExceptionalComponent } from './request-type-investment-exceptional/request-type-investment-exceptional.component';
import { RequestTypeInvestmentFhnwComponent } from './request-type-investment/request-type-investment-fhnw.component';
import { RequestTypeInvestmentSpzComponent } from './request-type-investment/request-type-investment-spz.component';
import { RequestTypeInvestmentComponent } from './request-type-investment/request-type-investment.component';
import { RequestTypeNonCatalogProductBudgetedComponent } from './request-type-non-catalog-product-budgeted/request-type-non-catalog-product-budgeted.component';
import { RequestTypeNonCatalogProductNonBudgetedComponent } from './request-type-non-catalog-product-non-budgeted/request-type-non-catalog-product-non-budgeted.component';
import { RequestTypeStrategicProjectComponent } from './request-type-strategic-project/request-type-strategic-project.component';
import { RequestTypeTestPositionInvestmentComponent } from './request-type-test-position-investment/request-type-test-position-investment.component';
import { RequestTypeTestPositionLuksComponent } from './request-type-test-position/request-type-test-position-luks.component';
import { RequestTypeTestPositionUszComponent } from './request-type-test-position/request-type-test-position-usz.component';
import { RequestTypeTestPositionComponent } from './request-type-test-position/request-type-test-position.component';

@AutoUnsubscribe()
@Injectable()
export class DemandRequestDetailService {
  public requestTypeComponents: {
    id: RequestTypeEnum;
    component: Type<RequestTypeBaseComponent>;
  }[] = [
    {
      id: RequestTypeEnum.InvestmentExceptional,
      component: RequestTypeInvestmentExceptionalComponent,
    },
    {
      id: RequestTypeEnum.Investment,
      component: RequestTypeInvestmentComponent,
    },
    {
      id: RequestTypeEnum.AdvancedTraining,
      component: RequestTypeAdvancedTrainingComponent,
    },
    {
      id: RequestTypeEnum.ApplianceLoaner,
      component: RequestTypeApplianceLoanerComponent,
    },
    {
      id: RequestTypeEnum.CatalogExtension,
      component: RequestTypeCatalogExtensionComponent,
    },
    {
      id: RequestTypeEnum.CatalogProduct,
      component: RequestTypeCatalogProductComponent,
    },
    {
      id: RequestTypeEnum.HumanResourceRequirement,
      component: RequestTypeHumanResourceRequirementComponent,
    },
    {
      id: RequestTypeEnum.InstrumentsLoaner,
      component: RequestTypeInstrumentsLoanerComponent,
    },
    {
      id: RequestTypeEnum.NonCatalogProductBudgeted,
      component: RequestTypeNonCatalogProductBudgetedComponent,
    },
    {
      id: RequestTypeEnum.NonCatalogProductNonBudgeted,
      component: RequestTypeNonCatalogProductNonBudgetedComponent,
    },
    {
      id: RequestTypeEnum.TestPosition,
      component: RequestTypeTestPositionComponent,
    },
    {
      id: RequestTypeEnum.TestPositionInvestment,
      component: RequestTypeTestPositionInvestmentComponent,
    },
    {
      id: RequestTypeEnum.StrategicProject,
      component: RequestTypeStrategicProjectComponent,
    },
  ];

  subs = new SubSink();

  formFieldAppearance = 'outline';

  costCenterControl = new FormControl();
  accountControl = new FormControl();
  supervisorControl = new FormControl();
  form = new FormGroup({
    costCenterId: this.costCenterControl,
    account: this.accountControl,
    supervisor: this.supervisorControl,
  });

  requestTypeForm = new FormGroup({});
  taskForm = new FormGroup({});
  complianceForm = new FormGroup({});

  options: FormlyFormOptions = {};
  isNew = false;
  processInstanceId = '';
  taskId = '';
  companyId: number | undefined;

  currency$ = new BehaviorSubject<string>('');
  model$ = new BehaviorSubject<RequestDto | null>(null);
  conversations$ = new BehaviorSubject<ConversationReducedDto[]>([]);

  compliance$ = new BehaviorSubject<RequestComplianceDTODto[] | null>(null);

  requestProducts$ = new BehaviorSubject<RequestProductDto[] | null>(null);

  isProcessActive$ = new BehaviorSubject<boolean>(false);
  pendingTasks$ = new BehaviorSubject<TaskReducedDto[]>([]);
  requestLog$ = new BehaviorSubject<RequestLogDto[]>([]);
  activeTask$ = new BehaviorSubject<TaskReducedDto | null>(null);

  requestTypeId$ = new BehaviorSubject<RequestTypeEnum | null>(null);

  showNewConversationForm$ = new BehaviorSubject<boolean>(false);

  createInvitationAllowed$ = this.requestTypeId$.pipe(
    map(m => {
      if (!m) return false;
      switch (m as RequestTypeEnum) {
        case RequestTypeEnum.ApplianceLoaner:
        case RequestTypeEnum.Investment:
        case RequestTypeEnum.InvestmentExceptional:
        case RequestTypeEnum.InstrumentsLoaner:
        case RequestTypeEnum.CatalogProduct:
        case RequestTypeEnum.NonCatalogProductBudgeted:
        case RequestTypeEnum.NonCatalogProductNonBudgeted:
        case RequestTypeEnum.CatalogExtension:
        case RequestTypeEnum.TestPosition:
        case RequestTypeEnum.TestPositionInvestment:
        case RequestTypeEnum.StrategicProject:
          return true;
        case RequestTypeEnum.AdvancedTraining:
        case RequestTypeEnum.HumanResourceRequirement:
          return false;
        default:
          return false;
      }
    })
  );

  public get isBuyerRequestTask$(): Observable<boolean> {
    return this.activeTask$.pipe(
      startWith(null),
      map(m => {
        if (this.isNew) return false;
        if (!m) return false;
        return m.formKey == TaskFormKeyEnum.BuyerRequest;
      }),
      shareReplay(1)
    );
  }

  public get isReadOnly$(): Observable<boolean> {
    return this.activeTask$.pipe(
      switchMap(activeTask =>
        this.hasPermissionIsRequestAdmin$.pipe(
          map(isRequestAdmin => {
            return { activeTask, isRequestAdmin };
          })
        )
      ),
      map(m => {
        if (this.isNew) return false;
        if (!m.activeTask) return true;
        if (m.isRequestAdmin && m.activeTask != null) return false;
        return !(
          m.activeTask.formKey == TaskFormKeyEnum.BuyerRequest ||
          m.activeTask.formKey == TaskFormKeyEnum.BuyerRequestEdit ||
          m.activeTask.formKey == TaskFormKeyEnum.BuyerRequestApprovalEdit
        );
      })
    );
  }

  public get approvalAllowed$(): Observable<boolean> {
    return this.activeTask$.pipe(
      switchMap(activeTask => {
        return this.hasPermissionIsRequestAdmin$.pipe(
          map(isRequestAdmin => {
            if (isRequestAdmin && activeTask != null) {
              activeTask.callerIsAssignee = true;
            }

            return activeTask;
          })
        );
      }),
      map(m => {
        if (!m) return false;
        return (
          (m.formKey == TaskFormKeyEnum.BuyerRequestApproval ||
            m.formKey == TaskFormKeyEnum.BuyerRequestApprovalEdit ||
            m.formKey == TaskFormKeyEnum.BuyerRequestComplianceApproval) &&
          (m.callerIsAssignee ?? false)
        );
      })
    );
  }

  public get delegateAllowed$(): Observable<'multiple' | 'single' | false> {
    return this.activeTask$.pipe(
      map(m => {
        if (!m) return false;
        if (m.formKey == TaskFormKeyEnum.BuyerRequestDelegate)
          return 'multiple';
        if (m.formKey == TaskFormKeyEnum.BuyerRequestDelegateSingle)
          return 'single';

        return false;
      })
    );
  }

  public get submitAllowed$(): Observable<boolean> {
    return this.activeTask$.pipe(
      map(m => {
        if (!m) return false;
        return m.formKey == TaskFormKeyEnum.BuyerRequest;
      })
    );
  }

  public get completeAllowed$(): Observable<boolean> {
    return this.activeTask$.pipe(
      switchMap(activeTask => {
        return this.hasPermissionIsRequestAdmin$.pipe(
          map(isRequestAdmin => {
            if (isRequestAdmin && activeTask != null) {
              activeTask.callerIsAssignee = true;
            }

            return activeTask;
          })
        );
      }),
      map(m => {
        if (!m) return false;
        return (
          (m.formKey == TaskFormKeyEnum.BuyerRequestEdit ||
            m.formKey == TaskFormKeyEnum.BuyerRequestComment ||
            m.formKey == TaskFormKeyEnum.BuyerRequestApproval ||
            m.formKey == TaskFormKeyEnum.BuyerRequestComplianceApproval ||
            m.formKey == TaskFormKeyEnum.BuyerRequestApprovalEdit ||
            m.formKey == TaskFormKeyEnum.BuyerRequestDelegateSingle ||
            m.formKey == TaskFormKeyEnum.BuyerRequestDelegate) &&
          (m.callerIsAssignee ?? false)
        );
      })
    );
  }

  public get commentAllowed$(): Observable<boolean> {
    return this.activeTask$.pipe(
      map(m => {
        if (!m) return false;
        return (
          m.formKey == TaskFormKeyEnum.BuyerRequestEdit ||
          m.formKey == TaskFormKeyEnum.BuyerRequestComment ||
          m.formKey == TaskFormKeyEnum.BuyerRequestApproval ||
          m.formKey == TaskFormKeyEnum.BuyerRequestComplianceApproval ||
          m.formKey == TaskFormKeyEnum.BuyerRequestApprovalEdit
        );
      })
    );
  }

  public get storeAllowed$(): Observable<boolean> {
    return this.activeTask$.pipe(
      map(m => {
        if (!m) return false;
        return m.formKey == TaskFormKeyEnum.BuyerRequest;
      }),
      switchMap(storeAllowed => {
        return this.hasPermissionIsRequestAdmin$.pipe(
          map(isRequestAdmin => {
            return isRequestAdmin || storeAllowed;
          })
        );
      })
    );
  }

  public pdfReportAllowed$ = new BehaviorSubject<boolean>(false);

  public get startConversationAllowed$(): Observable<boolean> {
    return this.activeTask$.pipe(
      switchMap(activeTask => {
        return this.hasPermissionIsRequestAdmin$.pipe(
          map(isRequestAdmin => {
            if (isRequestAdmin && activeTask != null) {
              activeTask.callerIsAssignee = true;
            }

            return activeTask;
          })
        );
      }),
      map(m => {
        if (!m) return false;
        return m.callerIsAssignee ?? false;
      })
    );
  }

  private currentCompanyId$ = this.userInfoService
    .isAuthenticated()
    .pipe(
      switchMap(isAuthenticated => {
        if (isAuthenticated) {
          return this.userInfoService
            .getUserInfo()
            .pipe(map(m => m?.company.id));
        } else {
          return this.model$.pipe(map(m => m?.companyId));
        }
      })
    )
    .pipe(
      tap(t => {
        // TODO: move this to backend

        const requestTypeComponentOverload: {
          id: RequestTypeEnum;
          component: Type<RequestTypeBaseComponent>;
        }[] = [];

        switch (t) {
          // KS Solzern Demo Firma
          case 65447577:
            this.pdfReportAllowed$.next(true);

            requestTypeComponentOverload.push({
              id: RequestTypeEnum.CatalogProduct,
              component: RequestTypeCatalogProductUszComponent,
            });

            requestTypeComponentOverload.push({
              id: RequestTypeEnum.TestPosition,
              component: RequestTypeTestPositionUszComponent,
            });
            // requestTypeComponentOverload.push({
            //     id: RequestTypeEnum.CatalogProduct,
            //     component: RequestTypeCatalogProductUszComponent,
            // });

            // requestTypeComponentOverload.push({
            //     id: RequestTypeEnum.TestPosition,
            //     component: RequestTypeTestPositionUszComponent,
            // });

            // requestTypeComponentOverload.push({
            //     id: RequestTypeEnum.Investment,
            //     component: RequestTypeInvestmentSpzComponent,
            // });

            // requestTypeComponentOverload.push({
            //     id: RequestTypeEnum.InvestmentExceptional,
            //     component:
            //         RequestTypeInvestmentExceptionalSpzComponent,
            // });
            break;
          // USZ
          case 65978168:
          case 67976021:
          case 72768623:
            if (t == 72768623) {
              //TODO: remove this when USZ is ready
              this.pdfReportAllowed$.next(true);
            }

            requestTypeComponentOverload.push({
              id: RequestTypeEnum.CatalogProduct,
              component: RequestTypeCatalogProductUszComponent,
            });

            requestTypeComponentOverload.push({
              id: RequestTypeEnum.TestPosition,
              component: RequestTypeTestPositionUszComponent,
            });
            break;
          // LUKS
          case 67148587:
          case 66091412:
            requestTypeComponentOverload.push({
              id: RequestTypeEnum.CatalogProduct,
              component: RequestTypeCatalogProductLuksComponent,
            });

            requestTypeComponentOverload.push({
              id: RequestTypeEnum.TestPosition,
              component: RequestTypeTestPositionLuksComponent,
            });

            break;
          // SPZ
          case 67230764: // PROD
          case 68599773: // UAT
          case 72728814: // DEV
            this.pdfReportAllowed$.next(true);

            requestTypeComponentOverload.push({
              id: RequestTypeEnum.Investment,
              component: RequestTypeInvestmentSpzComponent,
            });

            requestTypeComponentOverload.push({
              id: RequestTypeEnum.InvestmentExceptional,
              component: RequestTypeInvestmentExceptionalSpzComponent,
            });
            break;
          // FHNW
          case 72817577: // DEV
          case 68415878: // UAT
          case 101549015: // PROD
            requestTypeComponentOverload.push({
              id: RequestTypeEnum.Investment,
              component: RequestTypeInvestmentFhnwComponent,
            });
        }

        requestTypeComponentOverload.forEach(rt => {
          const tmp = this.requestTypeComponents.find(f => f.id == rt.id);

          if (tmp) tmp.component = rt.component;
        });
      })
    );

  requestTypeSelectList$ = this.currentCompanyId$.pipe(
    switchMap(x => this.requestService.getRequestTypesByCompanyId(x ?? -1)),
    map(m =>
      m
        .map(m1 => {
          return {
            key: m1.id,
            value: `${m1.name}`,
            data: m1 as any,
          };
        })
        .sort(byTextAscending(m => m.value))
    ),
    map(m => {
      m.unshift({
        key: undefined,
        value: this.translateService.instant(_('SHARED.EMPTY_OPTION')),
        data: null,
      });

      return m;
    })
  );

  costCenterSelectList$ = this.currentCompanyId$.pipe(
    switchMap(x => this.companyService.getCostCentersByCompanyId(x ?? -1)),
    map(m =>
      m
        .map(m1 => {
          return {
            key: m1.id,
            value: `${m1.number} ${m1.name}`,
            data: m1 as any,
          };
        })
        .sort(byTextAscending(m => m.value))
    ),
    map(m => {
      if (m.length > 0) {
        m.unshift({
          key: undefined,
          value: this.translateService.instant(_('SHARED.EMPTY_OPTION')),
          data: null,
        });
      }

      return m;
    })
  );

  rekoleCategorySelectList$ = this.accountingService.getRekoleCategories().pipe(
    map(m =>
      m
        .map(m1 => {
          return {
            key: `${m1.cd}` as any,
            value: `${m1.cd} ${m1.description}`,
            data: m1 as RekoleCategoryDto | null,
            id: `${m1.id}` as any,
          };
        })
        .sort(byTextAscending(m => m.value))
    ),
    map(m => {
      m.unshift({
        key: undefined,
        value: this.translateService.instant(_('SHARED.EMPTY_OPTION')),
        data: null,
        id: undefined,
      });

      return m;
    })
  );

  usersChipsList$ = this.companyService
    .getUsers()
    .pipe(
      map(m => m.sort(byTextAscending(m => `${m.firstName} ${m.lastName}`)))
    );

  isAuthenticated$ = this.userInfoService.isAuthenticated();

  hasPermissionToCreateProject$ = this.userInfoService.hasPermission(
    UserRolesEnum.RfxCreateproject
  );

  hasPermissionIsRequestAdmin$ = this.userInfoService.hasPermission(
    UserRolesEnum.RequestAdmin
  );

  constructor(
    private requestService: RequestsXatSvc,
    private companyService: CompaniesXatSvc,
    private userInfoService: UserInfoService,
    private translateService: TranslateService,
    private accountingService: AccountingXatSvc,
    private breadcrumbService: BreadcrumbService,
    private location: Location,
    private router: Router
  ) {}

  completeActiveTask(formValues: any): Observable<void> {
    let action$: Observable<TaskReducedDto>;

    const approveParam: ApproveTaskParamDto = {
      approved: formValues?.approve ?? false,
      comment: formValues?.comment ?? '',
    };

    switch (this.activeTask$.value?.formKey) {
      case TaskFormKeyEnum.BuyerRequestDelegate: {
        const delegateParam: DelegateTaskParamDto = {
          assignees: formValues?.nextAssignees ?? '',
        };

        action$ = this.requestService.delegateTask(
          this.processInstanceId,
          this.taskId,
          delegateParam
        );
        break;
      }
      case TaskFormKeyEnum.BuyerRequestDelegateSingle: {
        const delegateSingleParam: DelegateSingleTaskParamDto = {
          assignee:
            (Array.isArray(formValues?.nextAssignee)
              ? formValues?.nextAssignee[0]
              : formValues?.nextAssignee) ?? '',
        };

        action$ = this.requestService.delegateSingleTask(
          this.processInstanceId,
          this.taskId,
          delegateSingleParam
        );
        break;
      }
      case TaskFormKeyEnum.BuyerRequestApproval:
        action$ = this.requestService.approveTask(
          this.processInstanceId,
          this.taskId,
          approveParam
        );
        break;
      case TaskFormKeyEnum.BuyerRequestApprovalEdit:
      case TaskFormKeyEnum.BuyerRequestComplianceApproval:
        action$ = this.requestService.approveTaskAndUpdateVariables(
          this.processInstanceId,
          this.taskId,
          approveParam
        );
        break;
      default:
        action$ = this.requestService.completeTaskAndUpdateVariables(
          this.processInstanceId,
          this.taskId,
          formValues?.comment ?? ''
        );
        break;
    }

    return action$.pipe(
      map(m => m?.taskId ?? ''),
      switchMap(m => this.loadRequest(this.processInstanceId, m)),
      tap(() => {
        const url = this.router
          .createUrlTree([
            DemandRequestRouteNames.ModuleBase,
            DemandRequestRouteNames.Detail,
            this.processInstanceId,
            this.taskId,
          ])
          .toString();

        this.location.go(url);
      })
    );
  }

  updateDocuments(documentIds: number[]): Observable<void> {
    return this.requestService
      .updateRequestDocuments(this.processInstanceId, documentIds)
      .pipe(map(() => {}));
  }

  saveRequest(
    formValues: RequestDto,
    preventReload?: boolean
  ): Observable<void> {
    if (this.model$.value == null) return EMPTY;

    // set empty string to null, because backend does not accept empty string for number fields
    let k: keyof typeof formValues;
    for (k in formValues) {
      const v = formValues[k];

      if (typeof v === 'string' && v === '') {
        (formValues as any)[k] = null;
      }
    }

    Object.assign(this.model$.value, formValues);

    // set task id on request, needed for logging purpose only
    if (!this.model$.value.taskId) this.model$.value.taskId = this.taskId;

    const saveRequest$ =
      this.processInstanceId !== ''
        ? this.requestService.updateRequest(
            this.processInstanceId,
            this.model$.value
          )
        : this.requestService.createRequest(this.model$.value);

    if (preventReload) return saveRequest$.pipe(map(m => {}));

    return saveRequest$.pipe(
      map(m => {
        return {
          processInstanceId: m.id ?? '',
          taskId: m.taskId ?? this.taskId,
        };
      }),
      switchMap(m => this.loadRequest(m.processInstanceId, m.taskId)),
      tap(() => {
        const url = this.router
          .createUrlTree([
            DemandRequestRouteNames.ModuleBase,
            DemandRequestRouteNames.Detail,
            this.processInstanceId,
            this.taskId,
          ])
          .toString();

        this.location.go(url);
      })
    );
  }

  loadRequest(processInstanceId: string, taskId: string): Observable<void> {
    if (processInstanceId !== '') {
      this.processInstanceId = processInstanceId;
      this.taskId = taskId;

      return combineLatest([
        this.requestService.getRequest(processInstanceId),
        this.requestService.isProcessActive(processInstanceId),
        this.requestService.getActiveTasksByProcess(processInstanceId),
        this.requestService.getRequestLog(processInstanceId, 0),
        this.requestService.getComplianceChecks(processInstanceId),
        this.rekoleCategorySelectList$,
      ]).pipe(
        switchMap(
          ([
            request,
            isActive,
            pendingTasks,
            logs,
            complianceChecks,
            rekoleCategory,
          ]) => {
            this.form.reset();
            this.requestTypeForm.reset();
            this.taskForm.reset();

            this.isNew = false;

            // transform id based rekoleCds to code based rekoleCds
            if (
              request != null &&
              request.rekoleCd !== null &&
              request.rekoleCd !== undefined &&
              request.rekoleCd !== ''
            ) {
              const rekoleByCd = rekoleCategory.find(
                f => f.key === request.rekoleCd
              );

              if (!rekoleByCd) {
                const rekoleById = rekoleCategory.find(
                  f => f.id === request.rekoleCd
                );

                if (rekoleById) {
                  request.rekoleCd = rekoleById.key;
                }
              }
            }

            this.companyId = request.companyId;

            this.model$.next(request);
            this.compliance$.next(complianceChecks);

            this.breadcrumbService.set('@requestTitle', request.title ?? '');

            this.isProcessActive$.next(isActive);
            this.pendingTasks$.next(pendingTasks);

            if (isActive && !this.taskId) {
              if (pendingTasks.length > 0)
                this.taskId = pendingTasks[0].taskId ?? '';
            }

            let activeTask = pendingTasks.find(f => f.taskId === this.taskId);

            if (!activeTask && pendingTasks.length > 0)
              activeTask = pendingTasks[0];

            if (activeTask && activeTask.taskId != this.taskId) {
              // user used an old link, redirect to active task
              this.router.navigate([
                DemandRequestRouteNames.ModuleBase,
                DemandRequestRouteNames.Detail,
                activeTask.processInstanceId,
                activeTask.taskId,
              ]);
            }

            activeTask
              ? this.activeTask$.next(activeTask)
              : this.activeTask$.next(null);

            return request.companyId !== undefined
              ? this.companyService.getCurrencyByCompanyId(request.companyId)
              : this.companyService.getCurrency();
          }
        ),
        map(currencyDto => {
          this.currency$.next(currencyDto.currency ?? '');
        })
      );
    } else {
      this.isNew = true;

      this.breadcrumbService.set(
        '@requestTitle',
        this.translateService.instant('DEMAND_REQUEST.DETAIL.NEW_TITLE')
      );

      return combineLatest([
        this.companyService.getCurrency(),
        this.userInfoService.getUserInfo(),
      ]).pipe(
        map(([currency, userInfo]) => {
          this.currency$.next(currency.currency ?? '');
          this.model$.next({
            lastname: userInfo?.lastName,
            firstname: userInfo?.firstName,
            creationUser: userInfo?.id,
          });
        })
      );
    }
  }

  createInvitaton(): Observable<void> {
    return this.model$.pipe(
      switchMap(m => {
        if (m)
          return this.requestService.createProject(this.processInstanceId, m);
        return EMPTY;
      }),
      map(t => {
        this.router.navigate([
          InvitationRouteNames.ModuleBase,
          InvitationRouteNames.Detail,
          t.id,
        ]);
      })
    );
  }

  loadRequestLogs(): Observable<void> {
    return this.model$.pipe(
      switchMap(m => {
        if (m != null && m.id != null) {
          return this.requestService.getRequestLog(
            this.processInstanceId,
            m.id
          );
        }

        return EMPTY;
      }),
      tap(logs => {
        this.requestLog$.next(logs);
      }),
      map(() => {})
    );
  }

  loadConversations(): Observable<void> {
    return this.model$.pipe(
      switchMap(m => {
        if (m != null && m.id != null) {
          return this.requestService.getConversations(m.id);
        }

        return EMPTY;
      }),
      map(conversations => {
        this.conversations$.next(conversations);
      })
    );
  }

  loadConversation(conversationId: number): Observable<ConversationDto> {
    return this.model$.pipe(
      switchMap(m => {
        if (m != null && m.id != null) {
          return this.requestService.getConversation(m.id, conversationId);
        }

        return EMPTY;
      })
    );
  }

  showConversationForm(): void {
    this.showNewConversationForm$.next(true);
  }

  startConversation(params: StartConversationParamDto): Observable<void> {
    return this.model$.pipe(
      take(1),
      switchMap(m => {
        if (m != null && m.id != null) {
          return this.requestService.startConversation(
            this.companyId!,
            this.processInstanceId,
            m.id,
            params
          );
        }

        return EMPTY;
      }),
      switchMap(() => this.loadConversations()),
      switchMap(() => this.loadRequestLogs())
    );
  }

  replyToConversation(
    conversationId: number,
    message: string
  ): Observable<any> {
    return this.model$.pipe(
      take(1),
      switchMap(m => {
        if (m != null && m.id != null) {
          return this.requestService.replyToConversation(
            this.companyId!,
            this.processInstanceId,
            m.id,
            conversationId,
            message
          );
        }

        return EMPTY;
      })
    );
  }
}
